import { Box, Button, Typography } from '@mui/material';
import MinimizeIcon from '@mui/icons-material/Minimize';
import bridget_avatar from '@assets/bridget_avatar.png';
import BridgetLogo from '@assets/bridgetLogo.svg';
import dayjs from 'dayjs';
import { useOwnProfileQuery } from '@common/cache';
import { t } from 'i18next';
import { BubbleLoader } from 'components/Loaders';
import { forwardRef } from 'react';
import Linkify from 'linkify-react';
import { Opts as LinkifyOptions } from 'linkifyjs';
import { useTwemoji } from '../hooks/useTwemoji';

interface IBridgetHeaderProps {
  handleClose: () => void;
}

export const BridgetHeader = ({ handleClose }: IBridgetHeaderProps) => {
  return (
    <Box
      sx={{
        display: 'flex',
        position: 'relative',
        justifyContent: 'space-between',
        width: '100%',
        padding: '0',
        py: '1rem',
        px: '1.5rem',
        bgcolor: theme => theme.palette.primary.main,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <img
          width={'48px'}
          height={'48px'}
          src={bridget_avatar}
          style={{
            objectFit: 'cover',
          }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', pl: '1rem' }}>
          <Typography variant='body1'>{t('bridget.title')}</Typography>
          <Typography
            variant='body2'
            sx={{ color: theme => theme.palette.common.white }}
          >
            {t('bridget.subtitle')}
          </Typography>
        </Box>
      </Box>

      <Box
        onClick={handleClose}
        sx={{
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          ':hover': {
            cursor: 'pointer',
            bgcolor: 'rgba(32,32,32, .3)',
            borderRadius: '6px',
          },
          height: '30px',
          width: '30px',
        }}
      >
        <MinimizeIcon />
      </Box>
    </Box>
  );
};

interface IChatToggleButtonProps {
  isOpen: boolean;
  handleToggle: () => void;
}

export const ChatToggleButton = forwardRef<
  HTMLButtonElement,
  IChatToggleButtonProps
>(({ handleToggle, isOpen }, ref) => {
  return (
    <Box
      sx={{
        background:
          'linear-gradient(225deg, #008B5B, #ABE1CE, #133F6E, #008B5B)',
        backgroundSize: '300%',
        width: '172px',
        p: '2px',
        animation: 'linear-bg 3s infinite alternate',
      }}
    >
      <Button
        id='bridget-toggle-button'
        ref={ref}
        sx={{
          display: 'flex',
          height: 40,
          cursor: 'pointer',
          width: '100%',
          bgcolor: theme =>
            isOpen
              ? theme.palette.selectedButton.main
              : theme.palette.common.white,
          boxSizing: 'border-box',
          ':hover': {
            bgcolor: theme => theme.palette.grey[300],
          },
        }}
        onClick={() => handleToggle()}
        startIcon={<img src={BridgetLogo} />}
      >
        {t('bridget.askBridget')}
      </Button>
    </Box>
  );
});

ChatToggleButton.displayName = 'ChatToggleButton';

interface IChatEntryProps {
  message: string;
  time?: Date;
  isOwnMessage: boolean;
  linkifyOptions: LinkifyOptions;
  isBridgetTyping?: boolean;
}

export const ChatEntry = ({
  message,
  time,
  isOwnMessage,
  linkifyOptions,
  isBridgetTyping = false,
}: IChatEntryProps) => {
  const { data = { image: { thumbnail_xsmall: '' } } } = useOwnProfileQuery();
  const twemojiRef = useTwemoji();

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        pb: '1.5rem',
        maxWidth: '100%',
      }}
    >
      <img
        width={'24px'}
        height={'24px'}
        src={isOwnMessage ? data?.image?.thumbnail_xsmall : bridget_avatar}
        style={{
          objectFit: 'cover',
        }}
      />
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          pl: '0.5rem',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            width: '100%',
            gap: '1rem',
          }}
        >
          <Typography variant='body1' sx={{ color: 'text.primary' }}>
            {isOwnMessage
              ? t('bridget.youEntryTitle')
              : t('bridget.bridgetEntryTitle')}
          </Typography>
          {time && (
            <Typography
              variant='caption'
              sx={{
                color: theme => theme.palette.semiBlack.main,
                height: '100%',
                lineHeight: 'unset',
              }}
            >
              {dayjs(time).format('h:mm A')}
            </Typography>
          )}
        </Box>
        <Box sx={{ pt: '0.5rem' }}>
          {isBridgetTyping ? (
            <BubbleLoader />
          ) : (
            <Typography
              ref={twemojiRef}
              variant='body2'
              sx={{
                whiteSpace: 'pre-line',
                wordBreak: 'break-word',
                color: 'text.primary',
                '& img.emoji': {
                  height: '1em',
                  width: '1em',
                  verticalAlign: '-0.1em',
                },
              }}
            >
              <Linkify options={linkifyOptions}>{message}</Linkify>
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};
